import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, X, Hash, AtSign, Tag, Loader2, Save, Zap, Bookmark, Link2, Unplug } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

function TagField({ label, icon: Icon, items, setItems, placeholder, testid, prefix }) {
  const [value, setValue] = useState("");
  const add = () => {
    const v = value.trim().replace(/^[@#]/, "");
    if (!v) return;
    if (items.includes(v)) { setValue(""); return; }
    setItems([...items, v]);
    setValue("");
  };
  return (
    <div className="bg-[#121721] border border-white/10 rounded-2xl p-5">
      <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-400 mb-3">
        <Icon className="w-4 h-4 text-emerald-400" /> {label}
      </label>
      <div className="flex gap-2 mb-3">
        <Input
          data-testid={testid}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="bg-[#0F141C] border-white/10 text-zinc-100 h-11 rounded-xl focus-visible:ring-emerald-500"
        />
        <Button
          type="button"
          data-testid={`${testid}-add`}
          onClick={add}
          className="h-11 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#06231a] transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.length === 0 && <span className="text-sm text-zinc-600">Nothing added yet.</span>}
        {items.map((it) => (
          <motion.span
            key={it}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-1.5 text-sm font-mono text-zinc-200 bg-[#1A212E] border border-white/10 pl-3 pr-2 py-1 rounded-full"
          >
            {prefix}{it}
            <button onClick={() => setItems(items.filter((x) => x !== it))} className="text-zinc-500 hover:text-rose-300 transition-colors" aria-label="remove">
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.span>
        ))}
      </div>
    </div>
  );
}

export default function Inputs() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [xConn, setXConn] = useState(null);
  const [xBusy, setXBusy] = useState(false);

  useEffect(() => {
    api.get("/inputs").then(({ data }) => {
      setTopics((data.topics || []).map((t) => t.replace(/^[@#]/, "")));
      setKeywords((data.keywords || []).map((k) => k.replace(/^[@#]/, "")));
      setAccounts((data.x_accounts || []).map((a) => a.replace(/^@/, "")));
      setNotes(data.notes || "");
    }).finally(() => setLoading(false));
    api.get("/x/status").then(({ data }) => setXConn(data)).catch(() => {});
  }, []);

  const connectX = async () => {
    setXBusy(true);
    try {
      const { data } = await api.get("/x/connect");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not start X connection.");
      setXBusy(false);
    }
  };

  const disconnectX = async () => {
    setXBusy(true);
    try {
      await api.post("/x/disconnect");
      setXConn({ connected: false });
      toast("Disconnected X account");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setXBusy(false);
    }
  };

  const save = async (thenForge = false) => {
    setSaving(true);
    try {
      await api.put("/inputs", { topics, keywords, x_accounts: accounts, notes });
      if (thenForge) {
        toast.info("Saved. Forging your feed…");
        await api.post("/feed/refresh");
        toast.success("Feed forged. Jumping to your clips.");
        navigate("/feed");
      } else {
        toast.success("Mashup saved");
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="h-96 grid place-items-center"><Loader2 className="w-7 h-7 text-emerald-400 animate-spin" /></div>;

  return (
    <div className="max-w-4xl mx-auto">
      <p className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-2">tune your signal</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-50 mb-2">The Mashup</h1>
      <p className="text-zinc-400 mb-8 max-w-2xl leading-relaxed">
        Claude blends these with your X.com feed to decide what matters and how it's framed. The more specific your notes, the sharper your clips.
      </p>

      {/* Connect X for bookmarks */}
      <div className="bg-[#121721] border border-white/10 rounded-2xl p-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="x-connect-card">
        <div className="w-11 h-11 rounded-xl bg-sky-500/15 border border-sky-500/30 grid place-items-center shrink-0">
          <Bookmark className="w-5 h-5 text-sky-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-zinc-100">Your X Bookmarks</h3>
          {xConn?.connected ? (
            <p className="text-sm text-emerald-400 font-mono truncate" data-testid="x-connected-label">
              Connected as @{xConn.username} — bookmarks included on refresh
            </p>
          ) : (
            <p className="text-sm text-zinc-500">Connect X to pull your saved bookmarks in as source material.</p>
          )}
        </div>
        {xConn?.connected ? (
          <Button
            data-testid="x-disconnect-button"
            onClick={disconnectX}
            disabled={xBusy}
            variant="outline"
            className="h-11 rounded-xl border-white/10 bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white transition-colors shrink-0"
          >
            {xBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unplug className="w-4 h-4 mr-2" />} Disconnect
          </Button>
        ) : (
          <Button
            data-testid="x-connect-button"
            onClick={connectX}
            disabled={xBusy}
            className="h-11 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-semibold transition-colors shrink-0"
          >
            {xBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />} Connect X
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <TagField label="Topics" icon={Tag} items={topics} setItems={setTopics} placeholder="e.g. Artificial Intelligence" testid="mashup-topic-input" prefix="" />
        <TagField label="Keywords" icon={Hash} items={keywords} setItems={setKeywords} placeholder="e.g. semiconductors" testid="mashup-keyword-input" prefix="#" />
      </div>

      <div className="mb-4">
        <TagField label="X.com accounts to follow" icon={AtSign} items={accounts} setItems={setAccounts} placeholder="e.g. Reuters (without @)" testid="mashup-account-input" prefix="@" />
      </div>

      <div className="bg-[#121721] border border-white/10 rounded-2xl p-5 mb-8">
        <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-400 mb-3">
          <Zap className="w-4 h-4 text-amber-400" /> Your notes & angle
        </label>
        <Textarea
          data-testid="mashup-notes-textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What's your angle? e.g. 'I'm researching how AI affects creative jobs — favour practical, skeptical takes over hype.'"
          className="bg-[#0F141C] border-white/10 text-zinc-100 min-h-[120px] rounded-xl focus-visible:ring-emerald-500 leading-relaxed"
        />
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <Button
          data-testid="mashup-save-button"
          onClick={() => save(false)}
          disabled={saving}
          variant="outline"
          className="h-12 rounded-xl border-white/10 bg-transparent text-zinc-200 hover:bg-white/5 hover:text-white transition-colors"
        >
          <Save className="w-4 h-4 mr-2" /> Save mashup
        </Button>
        <Button
          data-testid="mashup-generate-digest-button"
          onClick={() => save(true)}
          disabled={saving}
          className="h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#06231a] font-semibold transition-colors active:scale-[0.98] flex-1 sm:flex-none sm:px-8"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
          Save & forge feed
        </Button>
      </div>
    </div>
  );
}
